import { useRef, useState, useLayoutEffect } from 'react';
import clsx from 'clsx';
import { useTextField } from 'react-aria';
import { Warning } from '../../../../icons/utility/Warning';
import { conformInputPropsToAria } from '../../../utils/conformInputPropsToAria';
import { getByPlaceholderText } from '../../../utils/text-input/getByPlaceholderText';
import { getInputType } from '../../../utils/text-input/getInputType';
import { phoneInputMask } from '../../../utils/text-input/phoneInputMask';
import { InputDetail } from '../../InputDetail/InputDetail';
import { LABEL_OFFSET, type Props } from '../TextInput';
import { InnerInputContainer } from './InnerInputContainer';
import { InputContainer } from './InputContainer';
import { PasswordMaskIcon } from './PasswordMaskIcon';
import { TextInputLabel } from './TextInputLabel';

import styles from '../TextInput.module.css';

type UncontrolledTextInputProps = Omit<Props, 'value' | 'onChange'>;

export function UncontrolledTextInput(props: UncontrolledTextInputProps) {
  const {
    label,
    placeholder,
    errorMessage,
    isDisabled,
    iconLeft,
    validateOnBlur,
    helpText,
    maxLength,
    defaultValue,
    className,
    isFocused: isFocusedProp,
    onBlur,
    type,
    isZipcode,
    textInputRef,
    initialMasked,
    onInvalid: onInvalidProp,
    onFocus: onFocusProp,
    showCharacterCount,
  } = props;
  const isPasswordInput =
    type === 'password' || props?.inputProps?.type === 'password';
  const isPhoneInput =
    (type === 'tel' || props?.inputProps?.type === 'tel') && !isZipcode;
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [passwordMasked, setPasswordMasked] = useState(
    initialMasked === undefined ? true : initialMasked
  );
  const inputType = getInputType(props, passwordMasked);
  const [isFocused, setIsFocused] = useState(false); // Don't use isFocusedProp as a default value. isFocused will get set below in a useLayoutEffect

  // If we use `@conform-to/react`, we can possibly remove this after refactoring the other forms
  // although it seems like labelProps wasn't exactly the same so I need to examine that more
  const { labelProps, inputProps: defaultInputProps } = useTextField(
    {
      ...conformInputPropsToAria(props.inputProps),
      ...props,
      type: inputType,
    },
    inputRef
  );

  // Remove value from inputProps
  const inputProps = { ...defaultInputProps, value: undefined };

  const defaultValueTransformed = isPhoneInput
    ? phoneInputMask(defaultValue || '')
    : defaultValue || '';

  const hasError = errorMessage !== undefined;
  const errorClass = hasError ? styles['is-error'] : undefined;
  const disabledClass = isDisabled ? styles['is-disabled'] : undefined;

  const [labelX, setLabelX] = useState<string | undefined>();

  useLayoutEffect(() => {
    if (containerRef.current === null || inputRef.current === null) {
      return;
    }

    const containerX = containerRef.current.getBoundingClientRect().x;
    const inputX = inputRef.current.getBoundingClientRect().x;
    const diff = containerX - inputX;
    setLabelX(`${diff + LABEL_OFFSET}px`);
  }, []);

  useLayoutEffect(() => {
    if (!isFocusedProp) {
      return;
    }

    inputRef?.current?.focus();
  }, [isFocusedProp]);

  const inputLength = inputRef?.current?.value?.length;
  const hasValue = inputLength !== undefined && inputLength > 0;
  const activeClass = hasValue || isFocused ? styles['is-active'] : undefined;

  return (
    <InputContainer textInputRef={textInputRef} className={className}>
      <div
        className={clsx(
          styles['input-group'],
          activeClass,
          errorClass,
          disabledClass
        )}
        ref={containerRef}
      >
        {iconLeft}
        <InnerInputContainer
          activeClass={activeClass}
          errorClass={errorClass}
          disabledClass={disabledClass}
        >
          <input
            {
              ...inputProps /* ARIA input props */
            }
            {
              ...(props.inputProps ||
                {}) /* Conform applies some input html attribute input props like onFocus, onBlur, etc.  */
            }
            /* if it's a password input, it will be 'password', unless the password input mask is disabled */
            type={inputType}
            onInvalid={(e) => {
              inputProps.onInvalid?.(e);
              onInvalidProp?.();
            }}
            onInput={(e) => {
              const target = e.target as HTMLInputElement;

              if (isPhoneInput && inputRef?.current) {
                inputRef.current.value = phoneInputMask(target.value);
              }
            }}
            defaultValue={defaultValueTransformed}
            ref={inputRef}
            className={clsx(
              styles.input,
              activeClass,
              errorClass,
              disabledClass
            )}
            placeholder={getByPlaceholderText(
              activeClass === styles['is-active'],
              label,
              placeholder
            )}
            onFocus={(e) => {
              setIsFocused(true);
              onFocusProp?.(e);
            }}
            onBlur={async (e) => {
              if (e.target.value) {
                validateOnBlur && (await validateOnBlur(e.target.value));
              }
              setIsFocused(false);
              onBlur?.(e);
            }}
          />
          <TextInputLabel
            {...{
              label,
              activeClass,
              errorClass,
              disabledClass,
              labelX,
              labelProps,
            }}
          />
        </InnerInputContainer>
        {hasError ? (
          <span className={styles['error-icon']}>
            <Warning />
          </span>
        ) : null}
        <PasswordMaskIcon
          isPasswordInput={isPasswordInput}
          passwordMasked={passwordMasked}
          onToggle={() => setPasswordMasked(!passwordMasked)}
        />
      </div>
      <InputDetail
        errorMessage={errorMessage}
        helpText={helpText}
        maxLength={maxLength}
        showCharacterCount={showCharacterCount}
        charCount={inputLength}
      />
    </InputContainer>
  );
}
